import React, { useEffect, useState, useCallback } from "react";
import { Container, Card, CardBody, Input, Button, Spinner, Row, Col } from "reactstrap";
import { Trophy, Search, Star, Mail, Phone, CheckCircle, Filter, X, ChevronLeft, ChevronRight, BarChart3, Briefcase, Calendar } from "lucide-react";
import Handler from "../../Helpers/Handler";
import AdminHeader from "./Header";
import "../../Assets/css/style.css";

const STAR_COUNT = 5;

const StarRating = ({ rating }) => (
  <div className="d-flex align-items-center gap-1">
    {Array.from({ length: STAR_COUNT }, (_, i) => {
      const filled = i + 1 <= Math.round(rating);
      return (
        <Star
          key={i}
          size={14}
          fill={filled ? "#f59e0b" : "transparent"}
          stroke={filled ? "#f59e0b" : "#cbd5e1"}
        />
      );
    })}
    <span className="ms-2 fw-bold text-navy small">
      {rating > 0 ? Number(rating).toFixed(1) : "—"}
    </span>
  </div>
);

const RankDisplay = ({ rank }) => {
  const isTopThree = rank <= 3;
  const colors = { 1: "#fbbf24", 2: "#3478d7", 3: "#92400e" };
  
  return (
    <div className="rank-container" style={{ backgroundColor: isTopThree ? colors[rank] : "#f1f5f9" }}>
      <span style={{ color: isTopThree ? "white" : "#64748b", fontWeight: "bold", fontSize: "0.85rem" }}>
        {rank === 1 ? "1st" : rank === 2 ? "2nd" : rank === 3 ? "3rd" : `#${rank}`}
      </span>
    </div>
  );
};

const VendorCard = ({ vendor, rank }) => {
  const initials = vendor.name?.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  const totalCompleted = parseInt(vendor.completed_deals || 0) + parseInt(vendor.completed_events || 0);
  const score = (parseFloat(vendor.avg_rating) * 0.6 + totalCompleted * 0.4).toFixed(1);
  const barWidth = Math.min(100, (parseFloat(vendor.avg_rating) / 5) * 100);

  return (
    <Card className={`tv-card-new border-0 shadow-sm rounded-4 h-100 ${rank <= 3 ? "top-rank-border" : ""}`}>
      <CardBody className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <RankDisplay rank={rank} />
          {parseFloat(vendor.avg_rating) >= 4.5 && (
            <CheckCircle size={18} className="text-success" />
          )}
        </div>

        <div className="text-center mb-3">
          <div className="tv-avatar-new mx-auto mb-2">{initials}</div>
          <h5 className="fw-bold text-navy mb-1 text-truncate">{vendor.name}</h5>
          <StarRating rating={parseFloat(vendor.avg_rating)} />
        </div>

        <div className="contact-info-grid mb-3">
            <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                <Mail size={12} /> <span className="text-truncate">{vendor.email || 'N/A'}</span>
            </div>
            <div className="d-flex align-items-center gap-2 text-muted small">
                <Phone size={12} /> <span>{vendor.mobile || 'N/A'}</span>
            </div>
        </div>

        <div className="stats-box rounded-3 p-2 d-flex justify-content-between text-center mb-3">
            <div className="flex-grow-1 border-end">
                <div className="fw-bold text-navy small">{vendor.total_reviews}</div>
                <div className="text-muted" style={{ fontSize: '0.65rem' }}>Reviews</div>
            </div>
            <div className="flex-grow-1 border-end">
                <div className="fw-bold text-navy small">{vendor.completed_deals}</div>
                <div className="text-muted" style={{ fontSize: '0.65rem' }}>Deals</div>
            </div>
            <div className="flex-grow-1">
                <div className="fw-bold text-navy small">{vendor.completed_events}</div>
                <div className="text-muted" style={{ fontSize: '0.65rem' }}>Events</div>
            </div>
        </div>

        <div className="score-section">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-muted fw-bold" style={{ fontSize: '0.7rem' }}>PERFORMANCE SCORE</span>
                <span className="text-navy fw-bold" style={{ fontSize: '0.8rem' }}>{score}</span>
            </div>
            <div className="progress-bg">
                <div className="progress-fill" style={{ width: `${barWidth}%` }}></div>
            </div>
        </div>
      </CardBody>
    </Card>
  );
};

const AdminTopVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [minCompleted, setMinCompleted] = useState(0);
  const [loading, setLoading] = useState(false);

  const limit = 12;

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    let url = `/top_vendors/top_vendor.php?page=${page}&limit=${limit}`;
    if (search) url += `&q=${encodeURIComponent(search)}`;
    if (minRating) url += `&min_rating=${minRating}`;
    if (minCompleted) url += `&min_completed=${minCompleted}`;

    const res = await Handler({ method: "GET", url });
    setLoading(false);
    if (res?.success) {
      setVendors(res.data?.list || []);
      setTotal(res.data?.pagination?.total || 0);
    }
  }, [page, search, minRating, minCompleted]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleSearch = () => { setPage(1); setSearch(searchInput); };
  const handleReset = () => {
    setSearchInput(""); setSearch("");
    setMinRating(0); setMinCompleted(0); setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-soft-blue min-vh-100 pb-5">
      <AdminHeader />
      <Container py="4" className="mt-4">
        
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div className="d-flex align-items-center mb-1">
                <div className="icon-box-navy me-2">
                    <Trophy size={20} />
                </div>
                <h3 className="fw-bold text-navy mb-0">Top Performing Vendors</h3>
            </div>
            <p className="text-muted small mb-0 ml-48">Rankings based on user ratings and transaction history</p>
          </div>
          <div className="text-navy fw-bold bg-white border px-3 py-2 rounded-3 shadow-sm">
             Total: {total}
          </div>
        </div>

        {/* Filter Toolbar */}
        <Card className="border-0 shadow-sm mb-4 rounded-4">
          <CardBody className="p-3">
            <Row className="g-3">
              <Col lg={4}>
                <div className="search-input-wrap">
                  <Search size={18} className="search-icon" />
                  <Input
                    className="form-control-clean"
                    placeholder="Search vendor name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
              </Col>
              <Col lg={3}>
                <div className="d-flex align-items-center gap-2">
                    <Star size={16} className="text-muted" />
                    <select
                        className="form-select border-0 bg-light small shadow-none"
                        value={minRating}
                        onChange={(e) => { setMinRating(parseFloat(e.target.value)); setPage(1); }}
                    >
                        <option value={0}>Any Rating</option>
                        <option value={4}>4.0★ & above</option>
                        <option value={4.5}>4.5★ & above</option>
                    </select>
                </div>
              </Col>
              <Col lg={3}>
                <div className="d-flex align-items-center gap-2">
                    <CheckCircle size={16} className="text-muted" />
                    <select
                        className="form-select border-0 bg-light small shadow-none"
                        value={minCompleted}
                        onChange={(e) => { setMinCompleted(parseInt(e.target.value)); setPage(1); }}
                    >
                        <option value={0}>Any Volume</option>
                        <option value={5}>5+ Completed</option>
                        <option value={10}>10+ Completed</option>
                    </select>
                </div>
              </Col>
              <Col lg={2} className="d-flex gap-2">
                <Button color="navy" className="w-100 btn-sm" onClick={handleSearch}>Apply</Button>
                {(search || minRating > 0 || minCompleted > 0) && (
                  <Button color="light" className="btn-sm border" onClick={handleReset}><X size={14}/></Button>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2 small">Loading leaderboard...</p>
          </div>
        ) : vendors.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-4 p-5 text-center">
            <div className="text-muted mb-3 opacity-50"><X size={48} className="mx-auto" /></div>
            <h5 className="fw-bold text-navy">No results found</h5>
            <Button color="link" onClick={handleReset} className="text-decoration-none small">Clear all filters</Button>
          </Card>
        ) : (
          <>
            <Row className="g-4">
              {vendors.map((v, i) => (
                <Col key={v.vendor_id} md={6} lg={4} xl={3}>
                  <VendorCard
                    vendor={v}
                    rank={(page - 1) * limit + i + 1}
                  />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-3 mt-5">
                <Button 
                    color="white" 
                    className="border shadow-sm btn-sm" 
                    disabled={page <= 1} 
                    onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-navy small fw-bold">Page {page} of {totalPages}</span>
                <Button 
                    color="white" 
                    className="border shadow-sm btn-sm" 
                    disabled={page >= totalPages} 
                    onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            )}
          </>
        )}
      </Container>

      <style>{`
        .bg-soft-blue { background-color: #f8fafc; }
        .text-navy { color: #1e3a8a; }
        .bg-navy { background-color: #1e3a8a; }
        .btn-navy { background-color: #1e3a8a; color: white; }
        .btn-navy:hover { background-color: #172554; color: white; }
        
        .icon-box-navy { 
            background: #1e3a8a; color: white; 
            width: 36px; height: 36px; 
            display: flex; align-items: center; justify-content: center; 
            border-radius: 10px; 
        }

        .rounded-4 { border-radius: 16px !important; }
        
        .search-input-wrap { position: relative; }
        .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .form-control-clean { 
            padding-left: 40px !important; 
            border: 0 !important; 
            background: #f1f5f9 !important;
            font-size: 0.85rem;
            height: 40px;
        }

        .tv-card-new { transition: transform 0.2s, box-shadow 0.2s; background: white; }
        .tv-card-new:hover { transform: translateY(-5px); box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1) !important; }
        .top-rank-border { border-top: 4px solid #1e3a8a !important; }

        .rank-container {
            width: 36px; height: 22px;
            display: flex; align-items: center; justify-content: center;
            border-radius: 6px;
        }

        .tv-avatar-new {
            width: 56px; height: 56px;
            background: #1e3a8a; color: white;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-weight: bold; font-size: 1.2rem;
            border: 4px solid #f0f4f8;
        }

        .stats-box { background: #f8fafc; border: 1px solid #f1f5f9; }
        .progress-bg { height: 6px; background: #f1f5f9; border-radius: 10px; overflow: hidden; }
        .progress-fill { height: 100%; background: #1e3a8a; border-radius: 10px; }

        .ml-48 { margin-left: 48px; }
        @media (max-width: 768px) { .ml-48 { margin-left: 0; } }
      `}</style>
    </div>
  );
};

export default AdminTopVendors;